/* eslint-disable no-useless-return */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProfileField, ProfileDataType } from '../../types/profile';
import { CustomFieldResponses, CustomFieldType, NetworkInviteLinkModel, NetworkInviteModel } from '../../types/invites';
import { connectionService, invitationService, profileService, communityService } from '../../services';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import { checkMandatoryFields } from '../../utils/validationUtils';
import useProfileFields from '../../hooks/useProfileFields';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import useDeleteParams from '../../hooks/useDeleteParams';
import { SuccessPageState } from '../../types/navigation';
import { createEmptyCustomFieldResponses } from '../../utils/formatUtils';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import { filterCustomLabelFields } from '../../utils/filterUtils';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';
import { SharedBusinessData } from '../../types/misc';
import NoRequestedDataCommunityInvitePage from '../common/NoRequestedDataCommunityInvitePage';
import { CommunityOverview } from '../../components/misc/CommunityOverview';

export default function AuthenticatedBusinessInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  useDeleteParams('link');

  const invitationId = params.get('invitationId');

  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [invite, setInvite] = useState<NetworkInviteModel | NetworkInviteLinkModel>();
  const [validate, setValidate] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!invitationId) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }

    invitationService
      .getNetworkInvite(invitationId)
      .then((inv) => {
        setInvite(inv);
        setCustomFieldResponses(createEmptyCustomFieldResponses(inv.customFields));
      })
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  const fetchedSharedData = useRef(false);
  useEffect(() => {
    if (!invite || profileFields.length === 0 || fetchedSharedData.current) return;
    fetchedSharedData.current = true;

    if (invite.businessId) {
      connectionService
        .getSharedDataWithBusiness(invite.businessId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    } else if (invite.communityId) {
      connectionService
        .getSharedDataWithCommunity(invite.communityId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    }
  }, [invite, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!invitationId || !invite) return;
    setValidate(true);

    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    if (!checkMandatoryFields(invite!, allSelectedData, customFieldResponses)) return;

    const args: SharedBusinessData = {
      propertyIds: allSelectedData.map((x): string => x.id!),
      customFieldResponses,
      dataForMembers: [],
    };

    await connectionService.createBusinessConnection(invitationId, args);

    try {
      await communityService.fetchCommunities();
    } catch (error) {
      console.error('Failed to refresh communities after accepting invite:', error);
    }

    const state: SuccessPageState = {
      translationKey: `page.common.success.message.${
        invite.communityId ? 'businessCommunityInvite' : 'businessInvite'
      }`,
      translationOptions: {
        businessName: invite.name,
      },
      networkInvite: invite,
    };
    if (invite.communityId && invite.communityFeatures?.EASY_SHARING)
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state, replace: true } });
    else {
      navigate('/success', { state });
      toast.success(t('toast.success.connectionCreated'));
    }
  };

  if (invite && !invite.dashboardFeatures?.MEMBER_ADMINISTRATION)
    return <NoRequestedDataCommunityInvitePage invite={invite!} onSubmit={handleSubmit} />;

  const communicationName = invite?.customFields?.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  return (
    <>
      <ResponsePageWrapper>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="font-semibold md:text-2xl">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">{t('page.businessInvite.description', { communityName: invite?.name })}</p>
          </div>

          {communicationName && (
            <div className="border-b border-secondary-50 pb-4">
              <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                {t('form.communicationName.label')} {communicationName?.mandatory && '*'}
              </h2>
              <CommunicationNameInput
                name={customFieldResponses[communicationName.customFieldId]}
                validate={validate}
                setName={(name) =>
                  setCustomFieldResponses({
                    ...customFieldResponses,
                    [communicationName.customFieldId]: name,
                  })
                }
                mandatory={communicationName.mandatory}
              />
            </div>
          )}

          <UserDataForm
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            mandatoryFields={invite?.mandatoryFields || []}
            nonRequestedFields={invite?.nonRequestedFields || []}
            onePerDataType
            onlyBirthDate
            preSelectAll
          />

          <div>
            {invite?.customFields && filterCustomLabelFields(invite?.customFields).length > 0 && (
              <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                {t('page.shared.requestedFields')}
              </h2>
            )}
            <CustomFieldsForm
              fields={invite?.customFields || []}
              response={customFieldResponses}
              setResponse={setCustomFieldResponses}
            />
          </div>
          <div className="flex h-full min-h-10 flex-1 items-end justify-end gap-2 align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <NoDataSharedModal
        open={modalOpen}
        setOpen={setModalOpen}
        requesterName={invite?.name || ''}
        handleComplete={handleSubmit}
      />
    </>
  );
}
